// src/Functions/pixel.js
const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const PHONE_PATTERN = /(?:\+?\d[\d\s\-().]{8,}\d)/g;
const CARD_PATTERN = /\b(?:\d[ -]*?){13,19}\b/g;
const TOKEN_PATTERN = /\b(?:eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+)\b/g;
const LONG_NUMBER_PATTERN = /\b\d{7,}\b/g;
const SENSITIVE_KEY_PATTERN = /(email|phone|name|address|password|token|card|cvv|cvc|ssn|social|iban|routing|account)/i;
const MAX_PARAM_STR_LEN = 80;
const MAX_QUEUE_SIZE = 180;
const MAX_QUEUE_ATTEMPTS = 120;
const DEDUPE_WINDOW_MS = 800;
const FLUSH_INTERVAL_MS = 500;
const PENDING_EVENTS_STORAGE_KEY = "cd_fbq_pending_events_v1";
const ATTRIBUTION_STORAGE_KEY = "cd_fbq_attribution_v1";
const ATTRIBUTION_TTL_MS = 1000 * 60 * 60 * 24 * 14;
const DEFAULT_PIXEL_ID = "722340759975289";
const ATTRIBUTION_KEYS = [
  "fbclid",
  "gclid",
  "gbraid",
  "wbraid",
  "ttclid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];

let flushTimer = null;
let eventCounter = 0;
let queueLoaded = false;
let lifecycleHooksBound = false;
let pixelInitialized = false;
const pendingEvents = [];
const dedupeMap = new Map();

const STANDARD_EVENTS = new Set([
  "PageView",
  "ViewContent",
  "Search",
  "Lead",
  "Contact",
  "CompleteRegistration",
  "InitiateCheckout",
  "AddPaymentInfo",
  "Purchase",
  "Subscribe",
  "StartTrial",
]);

const hasWindow = () => typeof window !== "undefined";

const isDevHost = () => {
  if (!hasWindow()) return false;
  const host = String(window.location.hostname || "").toLowerCase();
  return host === "localhost" || host === "127.0.0.1";
};

const userOptedOut = () => {
  if (!hasWindow()) return false;
  try {
    const local = String(localStorage.getItem("cd_analytics_opt_out") || "").toLowerCase();
    const session = String(sessionStorage.getItem("cd_analytics_opt_out") || "").toLowerCase();
    if (local === "true" || local === "1" || session === "true" || session === "1") return true;
  } catch {}

  try {
    if (navigator?.globalPrivacyControl === true) return true;
  } catch {}

  try {
    const dnt = String(
      navigator?.doNotTrack ||
      window?.doNotTrack ||
      navigator?.msDoNotTrack ||
      ""
    ).toLowerCase();
    if (dnt === "1" || dnt === "yes") return true;
  } catch {}

  return false;
};

const shouldTrack = () => (
  hasWindow() &&
  !userOptedOut() &&
  (!isDevHost() || process.env.REACT_APP_ENABLE_ANALYTICS_IN_DEV === "true")
);

const ensureFbqStub = () => {
  if (!hasWindow()) return;
  if (typeof window.fbq === "function") return;

  const fbq = function fbqProxy() {
    if (fbq.callMethod) {
      fbq.callMethod.apply(fbq, arguments);
      return;
    }
    fbq.queue.push(arguments);
  };

  fbq.push = fbq;
  fbq.loaded = false;
  fbq.version = "2.0";
  fbq.queue = [];

  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;
};

const injectPixelScript = () => {
  if (!hasWindow()) return;
  if (document.getElementById("cd-fbq-loader")) return;

  const script = document.createElement("script");
  script.id = "cd-fbq-loader";
  script.async = true;
  script.defer = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  script.onload = () => {
    try {
      if (typeof window.fbq === "function") {
        window.fbq.loaded = true;
      }
    } catch {}
    flushPending();
  };
  document.head.appendChild(script);
};

const initializePixel = () => {
  if (!hasWindow() || pixelInitialized || !shouldTrack()) return;
  const pixelId = String(process.env.REACT_APP_META_PIXEL_ID || DEFAULT_PIXEL_ID).trim();
  if (!pixelId) return;

  ensureFbqStub();
  injectPixelScript();

  try { window.fbq("init", pixelId); } catch {}
  try { window.fbq("track", "PageView"); } catch {}
  pixelInitialized = true;
};

const schedulePixelInitialization = () => {
  if (!hasWindow()) return;
  const run = () => {
    try {
      initializePixel();
    } catch {}
  };

  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(run, { timeout: 3000 });
    return;
  }

  window.setTimeout(run, 1200);
};

const stripPII = (value) => (
  String(value || "")
    .replace(EMAIL_PATTERN, "[redacted_email]")
    .replace(PHONE_PATTERN, "[redacted_phone]")
    .replace(CARD_PATTERN, "[redacted_card]")
    .replace(TOKEN_PATTERN, "[redacted_token]")
    .replace(LONG_NUMBER_PATTERN, "[redacted_number]")
);

const sanitizeString = (value, maxLen = MAX_PARAM_STR_LEN) => stripPII(value).slice(0, maxLen);

const sanitizeValue = (value, depth = 0) => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === "string") return sanitizeString(value);
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  if (typeof value === "boolean") return value;
  if (Array.isArray(value)) {
    if (depth >= 1) return undefined;
    return value
      .map((entry) => sanitizeValue(entry, depth + 1))
      .filter((entry) => entry !== undefined)
      .slice(0, 8);
  }
  if (typeof value === "object") {
    if (depth >= 1) return undefined;
    const out = {};
    Object.entries(value).forEach(([rawKey, rawValue]) => {
      const key = String(rawKey || "").trim().slice(0, 40);
      if (!key || SENSITIVE_KEY_PATTERN.test(key)) return;
      const sanitized = sanitizeValue(rawValue, depth + 1);
      if (sanitized !== undefined) out[key] = sanitized;
    });
    return Object.keys(out).length ? out : undefined;
  }
  return undefined;
};

const sanitizeParams = (params = {}) => {
  const out = {};
  Object.entries(params || {}).forEach(([rawKey, rawValue]) => {
    const key = String(rawKey || "").trim().slice(0, 40);
    if (!key || SENSITIVE_KEY_PATTERN.test(key)) return;
    const sanitized = sanitizeValue(rawValue);
    if (sanitized !== undefined) out[key] = sanitized;
  });
  return out;
};

const sanitizeOptions = (options = {}) => {
  const out = {};
  const eventId = String(options?.eventID || options?.eventId || "").trim();
  if (eventId) out.eventID = sanitizeString(eventId);
  return out;
};

const normalizeCustomEventName = (eventName) => {
  let normalized = String(eventName || "")
    .replace(/[^A-Za-z0-9_]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (!normalized) normalized = "CustomEvent";
  return normalized.slice(0, 40);
};

const makeStorageSafeJson = (value) => {
  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
};

const parseStorageJson = (raw) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const persistPendingQueue = () => {
  if (!hasWindow()) return;
  try {
    if (!pendingEvents.length) {
      sessionStorage.removeItem(PENDING_EVENTS_STORAGE_KEY);
      return;
    }
    const payload = pendingEvents.slice(-MAX_QUEUE_SIZE);
    const raw = makeStorageSafeJson(payload);
    if (raw) sessionStorage.setItem(PENDING_EVENTS_STORAGE_KEY, raw);
  } catch {}
};

const loadPersistedQueue = () => {
  if (!hasWindow() || queueLoaded) return;
  queueLoaded = true;
  try {
    const parsed = parseStorageJson(sessionStorage.getItem(PENDING_EVENTS_STORAGE_KEY));
    if (!Array.isArray(parsed)) return;
    parsed.forEach((entry) => {
      if (!entry || !Array.isArray(entry.args)) return;
      pendingEvents.push({
        args: entry.args,
        attempts: Number.isFinite(Number(entry.attempts)) ? Number(entry.attempts) : 0,
      });
    });
    if (pendingEvents.length > MAX_QUEUE_SIZE) {
      pendingEvents.splice(0, pendingEvents.length - MAX_QUEUE_SIZE);
    }
  } catch {}
};

const readCookie = (name) => {
  if (!hasWindow()) return "";
  try {
    const escaped = String(name).replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
    const m = document.cookie.match(new RegExp(`(^|;\\s*)${escaped}=([^;]*)`));
    return m ? decodeURIComponent(m[2]) : "";
  } catch {
    return "";
  }
};

const readStoredAttribution = () => {
  if (!hasWindow()) return {};
  try {
    const parsed = parseStorageJson(localStorage.getItem(ATTRIBUTION_STORAGE_KEY));
    if (!parsed || typeof parsed !== "object") return {};
    const savedAt = Number(parsed.savedAt || 0);
    if (!savedAt || (Date.now() - savedAt) > ATTRIBUTION_TTL_MS) {
      localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
      return {};
    }
    return (parsed.values && typeof parsed.values === "object") ? parsed.values : {};
  } catch {
    return {};
  }
};

const captureAttributionFromUrl = () => {
  if (!hasWindow()) return {};
  let captured = {};
  try {
    const params = new URLSearchParams(window.location.search || "");
    ATTRIBUTION_KEYS.forEach((key) => {
      const value = String(params.get(key) || "").trim();
      if (value) captured[key] = sanitizeString(value, 120);
    });
  } catch {
    captured = {};
  }

  const existing = readStoredAttribution();
  const merged = { ...existing, ...captured };
  try {
    if (Object.keys(merged).length) {
      localStorage.setItem(
        ATTRIBUTION_STORAGE_KEY,
        makeStorageSafeJson({ savedAt: Date.now(), values: merged })
      );
    }
  } catch {}
  return merged;
};

const getAttributionParams = () => {
  const urlOrStored = captureAttributionFromUrl();
  const fbp = sanitizeString(readCookie("_fbp"), 120);
  const fbc = sanitizeString(readCookie("_fbc"), 180);
  const out = { ...urlOrStored };
  if (fbp) out.fbp = fbp;
  if (fbc) out.fbc = fbc;
  return out;
};

const canSendNow = () => (
  shouldTrack() &&
  typeof window.fbq === "function"
);

const clearOldDedupeEntries = () => {
  const now = Date.now();
  dedupeMap.forEach((ts, key) => {
    if (now - ts > DEDUPE_WINDOW_MS * 2) dedupeMap.delete(key);
  });
};

const shouldDropDuplicate = (mode, eventName, params = {}) => {
  clearOldDedupeEntries();
  const signature = `${mode}|${eventName}|${JSON.stringify(params)}`;
  const now = Date.now();
  const prev = dedupeMap.get(signature) || 0;
  if (now - prev <= DEDUPE_WINDOW_MS) return true;
  dedupeMap.set(signature, now);
  return false;
};

const flushPending = () => {
  loadPersistedQueue();

  if (!pendingEvents.length) {
    if (flushTimer) {
      clearInterval(flushTimer);
      flushTimer = null;
    }
    persistPendingQueue();
    return;
  }

  if (!canSendNow()) {
    for (let i = pendingEvents.length - 1; i >= 0; i -= 1) {
      pendingEvents[i].attempts += 1;
      if (pendingEvents[i].attempts > MAX_QUEUE_ATTEMPTS) {
        pendingEvents.splice(i, 1);
      }
    }
    persistPendingQueue();
    return;
  }

  const toFlush = pendingEvents.splice(0, pendingEvents.length);
  toFlush.forEach((entry) => {
    try {
      window.fbq(...entry.args);
    } catch {}
  });
  persistPendingQueue();
};

const attachLifecycleFlushHooks = () => {
  if (!hasWindow() || lifecycleHooksBound) return;
  lifecycleHooksBound = true;

  const flushOnHidden = () => {
    try {
      if (document.visibilityState === "hidden") flushPending();
    } catch {}
  };

  window.addEventListener("pagehide", flushPending);
  window.addEventListener("beforeunload", flushPending);
  document.addEventListener("visibilitychange", flushOnHidden);
};

const enqueueOrSend = (...args) => {
  if (!shouldTrack()) return;
  loadPersistedQueue();
  attachLifecycleFlushHooks();

  if (canSendNow()) {
    try {
      window.fbq(...args);
      flushPending();
      return;
    } catch {}
  }

  if (pendingEvents.length >= MAX_QUEUE_SIZE) {
    pendingEvents.shift();
  }
  pendingEvents.push({ args, attempts: 0 });
  persistPendingQueue();

  if (!flushTimer) {
    flushTimer = setInterval(flushPending, FLUSH_INTERVAL_MS);
  }
};

const withEventId = (options = {}, prefix = "cd_evt") => {
  const safe = sanitizeOptions(options);
  if (safe.eventID) return safe;
  return { ...safe, eventID: makeEventId(prefix) };
};

const mergeEventParams = (params = {}) => (
  sanitizeParams({
    ...getAttributionParams(),
    ...params,
  })
);

export const fbqSafe = (...args) => {
  enqueueOrSend(...args);
};

export const track = (eventName, params = {}, options = {}) => {
  if (!shouldTrack()) return;
  initializePixel();
  const eventLabel = String(eventName || "").trim();
  if (!eventLabel) return;

  if (!STANDARD_EVENTS.has(eventLabel)) {
    trackCustom(eventLabel, params, options);
    return;
  }

  const safeParams = mergeEventParams(params);
  if (shouldDropDuplicate("track", eventLabel, safeParams)) return;
  const safeOptions = withEventId(options, `fb_${normalizeCustomEventName(eventLabel).toLowerCase()}`);
  fbqSafe("track", eventLabel, safeParams, safeOptions);
};

export const trackCustom = (eventName, params = {}, options = {}) => {
  if (!shouldTrack()) return;
  initializePixel();
  const normalized = normalizeCustomEventName(eventName);
  const safeParams = mergeEventParams(params);
  if (shouldDropDuplicate("trackCustom", normalized, safeParams)) return;
  const safeOptions = withEventId(options, `fbc_${normalized.toLowerCase()}`);
  fbqSafe("trackCustom", normalized, safeParams, safeOptions);
};

export const trackVirtualPage = (routeName = "", params = {}, options = {}) => {
  const safeRoute = sanitizeString(routeName || "unknown_route", 60);
  const safeParams = {
    route_name: safeRoute,
    page_path: hasWindow() ? window.location.pathname : "",
    page_location: hasWindow() ? window.location.href : "",
    ...params,
  };

  // For SPA route changes, fire a standard PageView and a route-aware custom event.
  track("PageView", safeParams, options);
  trackCustom("VirtualPageView", safeParams, options);
};

export const makeEventId = (prefix = "cd") => {
  eventCounter += 1;
  return `${prefix}_${Date.now().toString(36)}_${eventCounter.toString(36)}_${Math.random().toString(16).slice(2, 8)}`;
};

if (hasWindow()) {
  loadPersistedQueue();
  attachLifecycleFlushHooks();
  schedulePixelInitialization();
  setTimeout(flushPending, 300);
}
